// LibraryLogin — passwordless, one-tap. Type the email you bought with, you're in.
// No password fields anywhere. Gumroad-style.
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { usePortalAuth } from "../portal/PortalAuth";
import { MaskReveal, Reveal } from "../components/Reveal";
import { ArrowUpRight, BookOpen, Loader2, ShieldCheck } from "lucide-react";
import { identifyUser } from "../components/Analytics";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LibraryLogin() {
  const { user, checking, refresh } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in? Skip the door.
  useEffect(() => {
    if (!checking && user) {
      const dest = location.state?.from?.pathname || "/library";
      navigate(dest, { replace: true });
    }
  }, [checking, user, navigate, location.state]);

  const handleOpenShelf = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter the email you used at checkout.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/library/signin-by-email`,
        { email: email.trim().toLowerCase() },
        { withCredentials: true }
      );
      await refresh();
      identifyUser({ email: email.trim().toLowerCase(), role: "library_buyer" });
      toast.success("Welcome back. Your shelf is open.");
      const dest = location.state?.from?.pathname || "/library";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't open your shelf. Try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't open your shelf.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-ink pt-28 md:pt-36 pb-24" data-testid="library-login-page">
      <section className="container-edge max-w-5xl mx-auto">
        {/* Editorial masthead block — Studio-specific, NOT "portal" */}
        <div className="grid md:grid-cols-12 gap-y-10 gap-x-12 items-end mb-14 md:mb-20">
          <div className="md:col-span-7">
            <div className="overline text-ink/55" data-testid="library-login-kicker">
              M Media Studio · Your Shelf
            </div>
            <MaskReveal delay={0.05}>
              <h1 className="font-display uppercase text-5xl sm:text-6xl md:text-[8.5vw] leading-[0.85] mt-3">
                The <span className="serif-italic font-serif normal-case">reading room</span>.
              </h1>
            </MaskReveal>
            <p className="font-serif italic text-ink/70 text-lg md:text-xl mt-5 max-w-xl leading-snug">
              Type the email you used at checkout. We&apos;ll open your shelf. No password, no email round-trip — every edition you own is one tap away.
            </p>
          </div>
          <Reveal delay={0.15} className="md:col-span-5 hidden md:block">
            <div className="border-l border-ink/15 pl-8 py-2">
              <div className="overline text-ink/50 mb-3">What you&apos;ll find</div>
              <ul className="font-serif text-ink/80 space-y-2 text-[15px] leading-snug">
                <li className="flex items-start gap-3"><span className="text-ink/30 mt-1">—</span><span>Every guide, workbook &amp; mini-course you&apos;ve bought</span></li>
                <li className="flex items-start gap-3"><span className="text-ink/30 mt-1">—</span><span>In-browser reading, no downloads, no zip files</span></li>
                <li className="flex items-start gap-3"><span className="text-ink/30 mt-1">—</span><span>Lifetime access · 10% of every sale to charity partners</span></li>
              </ul>
            </div>
          </Reveal>
        </div>

        {/* Sign-in card — email only, one tap */}
        <div className="grid md:grid-cols-12 gap-y-10 gap-x-12">
          <form
            onSubmit={handleOpenShelf}
            className="md:col-span-7 bg-white border border-ink/15 p-7 md:p-10"
            data-testid="library-signin-form"
          >
            <div className="overline text-ink/55 mb-6">Studio · Open Shelf</div>

            <label className="block mb-8">
              <span className="overline text-ink/55 block mb-2">Your email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border-b border-ink/30 focus:border-ink py-3 font-serif text-lg outline-none transition-colors"
                placeholder="the address you bought with"
                autoComplete="email"
                inputMode="email"
                required
                autoFocus
                data-testid="library-email-input"
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-ink text-cream py-4 overline tracking-[0.3em] hover:bg-ink/90 transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
              data-testid="library-signin-submit"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Opening your shelf…</span>
                </>
              ) : (
                <>
                  <span>Open my shelf</span>
                  <ArrowUpRight className="h-4 w-4" />
                </>
              )}
            </button>

            <div className="mt-7 pt-5 border-t border-ink/10 flex items-start gap-3">
              <ShieldCheck className="h-4 w-4 text-ink/45 mt-[3px] flex-shrink-0" />
              <p className="text-[12px] text-ink/55 font-serif leading-snug">
                Your shelf is tied to the email you used at checkout. If you used a different email at Stripe, type that one. Can&apos;t remember? Check the receipt Stripe sent you.
              </p>
            </div>
          </form>

          {/* Right column — "Haven't bought yet" CTA */}
          <Reveal delay={0.2} className="md:col-span-5">
            <div className="border border-ink/15 bg-ink text-cream p-7 md:p-10 h-full flex flex-col">
              <BookOpen className="h-6 w-6 mb-6 opacity-70" />
              <div className="overline text-cream/60 mb-3">First time here?</div>
              <h2 className="font-display uppercase text-3xl md:text-4xl leading-[0.95] mb-5">
                Start with an <span className="serif-italic font-serif normal-case">edition</span>.
              </h2>
              <p className="font-serif text-cream/80 text-[15px] leading-snug mb-8 flex-1">
                Browse 45 editions from M Media Studio — guides, workbooks, and mini-courses from a decade of building brands. Every purchase opens your shelf automatically.
              </p>
              <Link
                to="/studio"
                className="inline-flex items-center justify-between border border-cream/30 px-6 py-4 overline tracking-[0.3em] hover:bg-cream hover:text-ink transition-colors"
                data-testid="library-browse-studio"
              >
                <span>Browse the Studio</span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>

        {/* Tiny editorial footer */}
        <p className="overline text-ink/40 text-center mt-16">
          M Media Studio · Newport Beach · Built for readers, not downloaders
        </p>
      </section>
    </div>
  );
}
